import { Button } from "@/components/ui/button"
import { ChevronRight } from "lucide-react"
import { CategoryDto, ProviderDto } from "@/api/api-client"
import { CategoryFilterItem } from "./CategoryFilterItem"
import { ProviderFilterItem } from "./ProviderFilterItem"
import { PriceRangeFilter } from "./PriceRangeFilter"
import { WeightRangeFilter } from "./WeightRangeFilter"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"

interface CatalogSidebarFilterProps {
  categories?: CategoryDto[]
  providers?: ProviderDto[]
  selectedCategoryId: number | null
  onSelectCategory: (id: number | null) => void
  selectedProviderIds: number[]
  onToggleProvider: (id: number | null) => void
  minPrice: string
  maxPrice: string
  onMinPriceChange: (val: string) => void
  onMaxPriceChange: (val: string) => void
  minWeight: string
  maxWeight: string
  onMinWeightChange: (val: string) => void
  onMaxWeightChange: (val: string) => void
  language: "es" | "en"
  text: {
    categories: string
    allCategories: string
    providers: string
    allProviders: string
    priceRange: string
    weightRange: string
    min: string
    max: string
  }
}

export const CatalogSidebarFilter = ({
  categories,
  providers,
  selectedCategoryId,
  onSelectCategory,
  selectedProviderIds,
  onToggleProvider,
  minPrice,
  maxPrice,
  onMinPriceChange,
  onMaxPriceChange,
  minWeight,
  maxWeight,
  onMinWeightChange,
  onMaxWeightChange,
  text,
}: CatalogSidebarFilterProps) => {
  const FilterHeader = ({ children }: { children: React.ReactNode }) => (
    <div className="mb-6 inline-block border-b-2 border-accent pb-1">
      <h3 className="industrial-font text-2xl uppercase leading-none tracking-tight text-foreground">{children}</h3>
    </div>
  )

  return (
    <div className="space-y-16">
      {/* Category Filter */}
      <div>
        <FilterHeader>{text.categories}</FilterHeader>
        <div className="space-y-1">
          <Button
            variant="ghost"
            className={cn(
              "h-10 w-full justify-between rounded-none px-4 text-sm font-bold transition-all duration-300",
              selectedCategoryId === null
                ? "bg-primary/80 font-black text-white dark:bg-primary/90"
                : "text-muted-foreground hover:bg-primary/10 hover:text-foreground dark:hover:bg-primary/20"
            )}
            onClick={() => onSelectCategory(null)}
          >
            {text.allCategories}
            <ChevronRight className={cn("h-4 w-4", selectedCategoryId === null ? "text-white" : "text-muted-foreground/50")} />
          </Button>
          <div className="mt-2 space-y-1">
            {categories?.map((cat) => (
              <CategoryFilterItem
                key={cat.id}
                cat={cat}
                selectedId={selectedCategoryId}
                onSelect={onSelectCategory}
                language={"es"} // Assuming default for now
              />
            ))}
          </div>
        </div>
      </div>

      {/* Provider Filter */}
      <div>
        <FilterHeader>{text.providers}</FilterHeader>
        <div className="space-y-1">
          <Button
            variant="ghost"
            className={cn(
              "mb-2 h-10 w-full justify-between rounded-none px-4 text-sm font-bold uppercase tracking-wider transition-all duration-300",
              selectedProviderIds.length === 0
                ? "border-l-4 border-primary bg-primary/10 font-black text-foreground"
                : "border-l-4 border-transparent text-muted-foreground hover:bg-primary/10 hover:text-foreground dark:hover:bg-primary/20"
            )}
            onClick={() => onToggleProvider(null)}
          >
            {text.allProviders}
            <ChevronRight className={cn("h-4 w-4", selectedProviderIds.length === 0 ? "text-primary" : "text-muted-foreground/50")} />
          </Button>
          <div className="space-y-1">
            {providers?.map((provider) => (
              <ProviderFilterItem
                key={provider.id}
                provider={provider}
                isSelected={selectedProviderIds.includes(provider.id)}
                onToggle={onToggleProvider}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Price Range Filter */}
      <div>
        <FilterHeader>{text.priceRange}</FilterHeader>
        <PriceRangeFilter
          min={minPrice}
          max={maxPrice}
          onMinChange={onMinPriceChange}
          onMaxChange={onMaxPriceChange}
          text={{ min: text.min, max: text.max }}
        />
      </div>

      {/* Weight Range Filter */}
      <div>
        <FilterHeader>{text.weightRange}</FilterHeader>
        <WeightRangeFilter
          min={minWeight}
          max={maxWeight}
          onMinChange={onMinWeightChange}
          onMaxChange={onMaxWeightChange}
          text={{ min: text.min, max: text.max }}
        />
      </div>
    </div>
  )
}

